/**
 * §-Favoriten (Haus-Paragraphen): ParagraphRef-Keys in localStorage.
 * Getrennt von Composer-Modus-Pins (`portalComposerPins` = mode/tool) — hier nur §-IDs.
 * Keine Namen, keine Mandanten-PII.
 */

import type { ParagraphRef } from './portalParagraphRef';

const STORAGE_PREFIX = 'arko-paragraph-favorites';

/** Sinnvolle Obergrenze für die leere-Leiste-Liste (~5–15). */
export const MAX_PARAGRAPH_FAVORITES = 15;

/** Storage-Key: user-id wenn Session, sonst lokal — analog composerPinsStorageKey. */
export function paragraphFavoritesStorageKey(userId?: string | null): string {
  const uid = typeof userId === 'string' ? userId.trim() : '';
  return uid ? `${STORAGE_PREFIX}:u:${uid}` : `${STORAGE_PREFIX}:local`;
}

function isFavoriteKey(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const key = value.trim().toLowerCase();
  if (!key || key.length > 80) return false;
  // key-Format aus portalParagraphRef: `${law|§}:${section}`
  return /^[§a-z0-9][a-z0-9._\-]*:.+$/i.test(key) || /^§:.+$/.test(key);
}

function sanitizeFavoriteKeys(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of raw) {
    if (!isFavoriteKey(item)) continue;
    const key = item.trim().toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(key);
    if (out.length >= MAX_PARAGRAPH_FAVORITES) break;
  }
  return out;
}

/** Rekonstruiert ParagraphRef aus gespeichertem Key (nur ID, kein Display-Name in Storage). */
export function paragraphRefFromFavoriteKey(key: string): ParagraphRef | null {
  const normalized = key.trim().toLowerCase();
  const colon = normalized.indexOf(':');
  if (colon <= 0 || colon === normalized.length - 1) return null;
  const lawPart = normalized.slice(0, colon);
  const section = normalized.slice(colon + 1).replace(/\s+/g, ' ').trim();
  if (!section || section === '…') return null;
  const lawUp = lawPart === '§' ? undefined : lawPart.toUpperCase();
  const label = lawUp ? `§ ${section} ${lawUp}` : `§ ${section}`;
  return {
    section,
    law: lawUp,
    label,
    key: `${lawUp ?? '§'}:${section}`.toLowerCase(),
  };
}

export function favoritesKeysToRefs(keys: readonly string[]): ParagraphRef[] {
  const out: ParagraphRef[] = [];
  for (const key of keys) {
    const ref = paragraphRefFromFavoriteKey(key);
    if (ref) out.push(ref);
  }
  return out;
}

export function loadParagraphFavorites(userId?: string | null): string[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(paragraphFavoritesStorageKey(userId));
    if (!raw) return [];
    return sanitizeFavoriteKeys(JSON.parse(raw) as unknown);
  } catch {
    return [];
  }
}

export function saveParagraphFavorites(keys: string[], userId?: string | null): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(
      paragraphFavoritesStorageKey(userId),
      JSON.stringify(sanitizeFavoriteKeys(keys)),
    );
  } catch {
    /* quota / private mode */
  }
}

export function isParagraphFavorite(keys: readonly string[], refKey: string): boolean {
  return keys.includes(refKey.trim().toLowerCase());
}

/**
 * Stern toggeln. Beim Hinzufügen: ans Ende; bei Overflow älteste (vorn) droppen.
 */
export function toggleParagraphFavorite(keys: string[], refKey: string): string[] {
  const id = refKey.trim().toLowerCase();
  if (!id || id.includes(':…') || id.endsWith(':…')) return keys;
  if (keys.includes(id)) return keys.filter((k) => k !== id);
  const next = [...keys, id];
  if (next.length > MAX_PARAGRAPH_FAVORITES) {
    return next.slice(next.length - MAX_PARAGRAPH_FAVORITES);
  }
  return next;
}
